/**
 * personModel — geometria do PEDESTRE (estilo bonequinho low-poly) + variações.
 *
 * O avatar é montado por PEÇAS (cada peça vira um InstancedMesh no Fleet). Peças
 * com `variant` só aparecem para quem tem aquela opção (cabelo/roupa/pés); o Fleet
 * mantém somente as variantes utilizadas nas listas densas. As cores por jogador
 * (pele, cabelo, roupa, pés) usam instanceColor (chave em `color`).
 *
 * appearance (pode vir do backend — "passar cores customizadas"):
 *   { hair, top, bottom, feet, colors:{ skin, hair, top, bottom, feet } }
 */
import * as THREE from 'three';
import { setAvatarDetail } from './lod.js';
import { buildAnatomy, PERSON_LANDMARKS } from './person/anatomy.js';
import { buildHair } from './person/hair.js';
import { buildGarments } from './person/garments.js';
import { buildFootwear } from './person/footwear.js';
import { shade } from './person/surface.js';
import { PersonDeformMaterial } from './person/deform.js';
import { finishSkin, finishHair, hairMaterial } from './person/finish.js';
import { PersonDiffuseMaterial } from './person/lighting.js';

const HIP_Y = PERSON_LANDMARKS.hipY; // altura do quadril (pivô das pernas) — usado p/ animar os pés

/* ----------------------------- paletas ----------------------------- */
export const SKIN = ['#f6d3b0', '#eebd97', '#d9a173', '#c08552', '#9c6b43', '#6f4a2f'];
const HAIR_COLORS = ['#1c1410', '#3a2618', '#5a3d23', '#0e0e0e', '#7a7a78', '#caa14a', '#a0432a'];
const GARMENT = ['#3ddc84', '#e6584d', '#f2c14e', '#5b8def', '#c879e6', '#39c6c6', '#ef8c3b', '#e8ecea'];
const BOTTOM_COLORS = ['#2b3a44', '#39495a', '#5a4632', '#23262b', '#41506b', '#6b6b6b', '#7a4a2f'];
const SHOE_COLORS = ['#141414', '#2a1d14', '#3a2a1a', '#22303a'];

const HAIRS = ['short', 'pompadour', 'spiky', 'long', 'curly', 'bald'];
const TOPS = ['regata', 'tshirt', 'shirt', 'blazer', 'dress']; // regata = sem manga; tshirt = camiseta c/ mangas curtas
const BOTTOMS = ['pants', 'shorts', 'skirt'];
const FEET = ['shoes', 'flipflops', 'sandals'];

const pick = (a) => a[(Math.random() * a.length) | 0];

/** Gera uma aparência aleatória (estável por jogador). `p.color` vira a cor da
 *  roupa de cima (mantém a identidade de cor do jogador). */
export function randomAppearance(p) {
  const top = pick(TOPS);
  const bottom = top === 'dress' ? 'none' : pick(BOTTOMS);
  const feet = pick(FEET);
  const skin = pick(SKIN);
  return {
    hair: pick(HAIRS),
    top,
    bottom,
    feet,
    colors: {
      skin,
      hair: pick(HAIR_COLORS),
      top: (p && p.color) || pick(GARMENT),
      bottom: pick(BOTTOM_COLORS),
      // cor do CALÇADO (o pé fica na cor da PELE; só os detalhes do calçado usam esta cor)
      feet: pick(feet === 'shoes' ? SHOE_COLORS : ['#e6584d', '#3ddc84', '#5b8def', '#f2c14e', '#2a1d14', '#e8ecea'])
    }
  };
}

/** Shared appearance catalog; game/studio change sampling, not identities/pivots. */
export function buildPersonParts(detail) {
  const previous = setAvatarDetail(detail);
  try { return buildPersonPartsInner(); }
  finally { setAvatarDetail(previous); }
}

function buildPersonPartsInner() {
  const colored = new PersonDiffuseMaterial({ color: 0xffffff, vertexColors: true });
  const dark = new THREE.MeshLambertMaterial({ color: '#14110f' });
  const deformMaterials={arm:new PersonDeformMaterial('arm'),leg:new PersonDeformMaterial('leg'),cloth:new PersonDeformMaterial('cloth')};
  const anatomy = buildAnatomy(), hair = buildHair(), clothes = buildGarments(anatomy.head), feet = buildFootwear();
  const hairFinish=hairMaterial();
  finishSkin(anatomy.head,'head');
  finishSkin(anatomy.armLeft,'arm');finishSkin(anatomy.armRight,'arm');
  finishSkin(anatomy.leg,'leg');finishSkin(feet.foot,'foot');
  for(const [style,geo] of Object.entries(hair))finishHair(geo,style);
  const parts = [];
  const add = (id, geo, color, extra = {}) => {
    parts.push({ id, geo, mat: color ? colored : dark, pos: [0,0,0], ...(color ? {color} : {}), ...extra });
  };
  add('head', anatomy.head, 'skin', {pos:[0,PERSON_LANDMARKS.headY,0],cast:true});
  add('face', anatomy.face, null);
  add('hip', shade(anatomy.hip,(x,y)=>y>.10?.8:1), 'bottom', {
    pos:[0,.9,0], hideWhen:[{variant:'top',option:'dress'},{variant:'bottom',option:'skirt'}]
  });
  for (const [side, suffix, arm] of [[-1,'L',anatomy.armLeft],[1,'R',anatomy.armRight]]) {
    add('arm.'+suffix, arm, 'skin', {pos:[side*PERSON_LANDMARKS.shoulderX,PERSON_LANDMARKS.shoulderY,0],anim:'arm'+suffix});
    add('leg.'+suffix, anatomy.leg, 'skin', {pos:[side*.17,HIP_Y,0],anim:'leg'+suffix,hideWhen:{variant:'bottom',option:'pants'}});
    add('foot.'+suffix, feet.foot, 'skin', {pos:[side*.17,.08,.06],anim:'foot'+suffix,hipY:HIP_Y,hideWhen:{variant:'feet',option:'shoes'}});
  }
  for (const [option,geo] of Object.entries(hair)) add('hair.'+option,geo,'hair',{variant:'hair',option,mat:hairFinish});
  for (const [option,geo] of Object.entries(clothes.tops)) add('top.'+option,geo,'top',{
    variant:'top',option,...(option==='dress'?{}:{hideWhen:{variant:'bottom',option:'skirt'}})
  });
  for (const [option,geo] of Object.entries(clothes.tucked)) add('top.'+option+'.tucked',geo,'top',{
    variant:'top',option,when:{bottom:'skirt'}
  });
  for (const [option,geo] of Object.entries(clothes.sleeves)) for (const [side,suffix] of [[-1,'L'],[1,'R']]) {
    add('sleeve.'+option+'.'+suffix,geo,'top',{pos:[side*PERSON_LANDMARKS.shoulderX,PERSON_LANDMARKS.shoulderY,0],anim:'arm'+suffix,variant:'top',option});
  }
  for (const option of ['pants','shorts']) for (const [side,suffix] of [[-1,'L'],[1,'R']]) {
    add('bottom.'+option+'.'+suffix,clothes[option],'bottom',{pos:[side*.17,HIP_Y,0],anim:'leg'+suffix,variant:'bottom',option});
  }
  add('bottom.skirt',clothes.skirt,'bottom',{variant:'bottom',option:'skirt'});
  for (const option of ['shoes','flipflops','sandals']) for (const [side,suffix] of [[-1,'L'],[1,'R']]) {
    add('feet.'+option+'.'+suffix,feet[option],'feet',{pos:[side*.17,.08,.06],anim:'foot'+suffix,hipY:HIP_Y,variant:'feet',option});
  }
  // All colored pieces carry vertex factors, including geometry without trims.
  for (const {geo,color} of parts) if (color && !geo.attributes.color) shade(geo);
  for (const part of parts) {
    if (part.anim==='armL'||part.anim==='armR') part.deform='arm';
    if (part.anim==='legL'||part.anim==='legR') part.deform='leg';
    if (part.id==='bottom.skirt'||part.id==='top.dress') {part.deform='cloth';part.anim='cloth';}
    if (part.deform) part.mat=deformMaterials[part.deform];
  }
  return parts;
}
